import React, { useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import PropTypes from 'prop-types'
import { FormContext, useForm } from 'react-hook-form'

import { BaseDialog } from 'components'
import {
  ActiveAuditConflictDialog,
  AutomationForm,
  SkeletonsForm,
} from './components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import useThirdPartyManagementStepper from 'hooks/useThirdPartyManagementStepper'

import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'

import buildSchema from './schema'

const AutomationModal = ({ open, setOpen }) => {
  const snackbar = useSnackbar()
  const [isSaving, setIsSaving] = useState(false)
  const [quizTemplateIds, setQuizTemplateIds] = useState([])
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState(null)

  const { refresh, companySupplier } = useThirdPartyManagementStepper()
  const companySupplierId = companySupplier?.id

  const { response, isLoading } = useFetch(
    service.dponet.suppliers.auditConfigs.show,
    { companySupplierId },
    [],
  )

  const auditConfig = response?.data?.companySupplierAuditConfig
  const isEdit = !!auditConfig?.id

  const schema = useMemo(
    () => buildSchema(auditConfig?.startDate),
    [auditConfig?.startDate],
  )

  const formMethods = useForm({
    validationSchema: schema,
    defaultValues: {
      startDate: null,
      recurrence: '',
      recurrencePeriodType: 'days',
      duration: '',
      durationPeriodType: 'days',
      active: false,
      quizTemplateIds: [],
    },
  })

  const { handleSubmit, reset, register, setValue, watch } = formMethods

  const { ACTIVE_STATUS, INACTIVE_STATUS } = constants.companySuppliers

  const { computeNextAuditDate, findConflictingActiveAudit } =
    helpers.thirdPartyManegementPartners

  const watchedStartDate = watch('startDate')
  const watchedRecurrence = watch('recurrence')
  const watchedRecurrencePeriodType = watch('recurrencePeriodType')

  const inputsChanged =
    !isEdit ||
    !moment(watchedStartDate).isSame(moment(auditConfig?.startDate), 'day') ||
    String(watchedRecurrence) !== String(auditConfig?.recurrence ?? '') ||
    watchedRecurrencePeriodType !== auditConfig?.recurrencePeriodType

  const nextAuditDate = inputsChanged
    ? computeNextAuditDate(
        watchedStartDate,
        watchedRecurrence,
        watchedRecurrencePeriodType,
      )
    : auditConfig?.nextAuditDate

  const submitToApi = async (data) => {
    try {
      setIsSaving(true)

      const apiCall = isEdit
        ? service.dponet.suppliers.auditConfigs.update
        : service.dponet.suppliers.auditConfigs.create

      await apiCall({
        companySupplierId,
        ...(isEdit && { companySupplierAuditConfigId: auditConfig.id }),
        startDate: helpers.thirdPartyManegementPartners.formatDate(
          data.startDate,
        ),
        recurrence: data.recurrence,
        recurrencePeriodType: data.recurrencePeriodType,
        duration: data.duration,
        durationPeriodType: data.durationPeriodType,
        status: data.active ? ACTIVE_STATUS : INACTIVE_STATUS,
        quizTemplateIds,
      })

      snackbar.open({
        message: isEdit
          ? 'Automação atualizada com sucesso'
          : 'Automação configurada com sucesso',
        variant: 'success',
      })

      refresh()
      setOpen(false)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar automação',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const onSubmit = async (data) => {
    if (
      data.active &&
      findConflictingActiveAudit(
        companySupplier?.companySupplierAudits,
        data.startDate,
      )
    ) {
      setPendingSubmitData(data)
      setConfirmationOpen(true)
      return
    }

    await submitToApi(data)
  }

  const handleConfirmActiveAuditFinalization = async () => {
    setConfirmationOpen(false)
    if (pendingSubmitData) {
      const data = pendingSubmitData
      setPendingSubmitData(null)
      await submitToApi(data)
    }
  }

  useEffect(() => {
    register({ name: 'quizTemplateIds' })
  }, [register])

  useEffect(() => {
    setValue('quizTemplateIds', quizTemplateIds)
  }, [quizTemplateIds, setValue])

  useEffect(() => {
    if (!auditConfig) return

    reset({
      startDate: auditConfig?.startDate || null,
      recurrence: String(auditConfig?.recurrence || ''),
      recurrencePeriodType: auditConfig?.recurrencePeriodType || 'days',
      duration: String(auditConfig?.duration || ''),
      durationPeriodType: auditConfig?.durationPeriodType || 'days',
      active: auditConfig?.status === ACTIVE_STATUS,
      quizTemplateIds:
        auditConfig.quizTemplates?.map((quiz) => quiz?.id).filter(Boolean) ||
        [],
    })

    const existingQuizTemplates =
      auditConfig.quizTemplates?.map((quiz) => quiz?.id).filter(Boolean) || []

    setQuizTemplateIds(existingQuizTemplates)
  }, [auditConfig])

  return (
    <>
      <BaseDialog
        open={open}
        setOpen={setOpen}
        title="Configurar Automação"
        dialogSize="md"
        fullWidth
        formRef="automation-modal-form"
        actionButtonText="Salvar"
        closeButtonText="Cancelar"
        loading={isLoading || isSaving}
        closeIconButton
      >
        {isLoading ? (
          <SkeletonsForm />
        ) : (
          <FormContext {...formMethods}>
            <AutomationForm
              onSubmit={handleSubmit(onSubmit)}
              nextAuditDate={nextAuditDate}
              quizTemplateIds={quizTemplateIds}
              setQuizTemplateIds={setQuizTemplateIds}
              auditConfig={auditConfig}
            />
          </FormContext>
        )}
      </BaseDialog>
      <ActiveAuditConflictDialog
        open={confirmationOpen}
        setOpen={setConfirmationOpen}
        onConfirm={handleConfirmActiveAuditFinalization}
        loading={isSaving}
      />
    </>
  )
}

AutomationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
}

export default AutomationModal
